import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import campaignsApi from 'common/src/modules/campaigns/campaigns.api';

const searchMatchingSignalsToolSchema = z.object({
  searchTerm: z.string().describe('Search term to find matching signals for product targeting'),
});

export default function searchMatchingSignalsTool(store: CLStore) {
  async function execute({
    input: {searchTerm},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof searchMatchingSignalsToolSchema,
    ProductCardScratchpadState
  >): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    if (!campaign) {
      return toolResult('Campaign is required to search matching signals');
    }

    if (!versionId) {
      return toolResult('Version ID is required to search matching signals');
    }

    if (!validatedScratchpad.card.feed) {
      return toolResult('Feed is required to search matching signals');
    }

    const signals = await campaignsApi.getSignalsByNameForProductCard({
      campaignId: campaign.id,
      signalName: searchTerm,
      versionId,
      productType: validatedScratchpad.card.feed.productType,
    });

    const signalsMetadata = signals.map((signal) => ({
      signalKey: signal.value,
      signalName: signal.name,
      category: signal.category,
    }));

    validatedScratchpad.card.availableSignals = [
      ...(validatedScratchpad.card.availableSignals || []),
      ...signalsMetadata,
    ];

    return toolResult(JSON.stringify({signals: validatedScratchpad.card.availableSignals}));
  }

  return tool({
    name: V2CardAgentToolName.SearchMatchingSignals,
    displayName: 'Searching for matching signals',
    description: 'Search for signals that match product targeting criteria',
    execute,
    inputSchema: searchMatchingSignalsToolSchema,
  });
}
